/**
 * History: where everyone stands, and what happened round by round.
 *
 * IT REPLACED AN N×N GRID. A "who owes who" matrix stops being readable at
 * about five players and this app supports 120 — the answer is two lists that
 * scale (your positions, and the society's) rather than a cell per pair.
 *
 * TWO QUESTIONS, TWO TABS. "Where do I stand against everyone?" is By player;
 * "what actually happened?" is By round. Both come from one request.
 *
 * READ-ONLY, AND THE WORDS MATTER. This is a ledger of POSITION: up, down,
 * level. Never owes, owed, pays or settle — and directional phrasing always
 * from the viewer's side ("you are down £35", "Up on Baz"). Colour never
 * carries the meaning on its own: every figure has a sign, a tag or a labelled
 * column beside it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '../useApi';
import { Avatar, Card, money } from './primitives';
import { formatRoundDate, shortName } from './format';
import {
  SORTS, barWidth, betSubline, betTitle, filteredRounds, myPositions,
  netOf, playerSubline, roundPill, roundScope, roundsLabel,
  roundsSummary,
  societyPositions,
  standing, standingRows, trend, trendCaption, vsOf,
} from './history';
import { PlayerSheet } from './PlayerSheet';

const TOP_N = 10;
const PLAYERS_N = 6;

const Eyebrow = ({ label, meta }) => (
  <div className="pr-eyebrow-row">
    <span className="pr-eyebrow">{label}</span>
    {meta ? <span className="pr-eyebrow-meta">{meta}</span> : null}
  </div>
);

const MoreToggle = ({ expanded, hidden, onToggle, showLabel }) => {
  if (hidden <= 0 && !expanded) return null;
  return (
    <button type="button" className="pr-more" onClick={onToggle}>
      {expanded ? showLabel : `Load ${hidden} more`}
    </button>
  );
};

const HistoryView = () => {
  const { authenticatedFetch } = useApi();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [view, setView] = useState('players');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('net');
  const [showAll, setShowAll] = useState(false);
  const [showAllMine, setShowAllMine] = useState(false);
  const [showAllSociety, setShowAllSociety] = useState(false);
  const [openRound, setOpenRound] = useState(null);
  const [betFilter, setBetFilter] = useState('all');
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/predictions/history');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setLoadError(null);
    } catch (err) {
      // A FAILED REQUEST IS NOT AN ANSWER — an empty ledger over a season that
      // exists is worse than saying so. See `home_blank_data_bug`.
      setLoadError('Could not load your history.');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => { load(); }, [load]);

  const myPlayerId = data?.me?.player_id ?? null;
  const nameOf = useCallback((id) => (data?.players || []).find(
    (p) => p.player_id === id)?.name || 'Player', [data]);

  const season = useMemo(
    () => standing(data, myPlayerId), [data, myPlayerId]);
  const points = useMemo(() => trend(data), [data]);
  const mine = useMemo(
    () => myPositions(data, myPlayerId), [data, myPlayerId]);
  const society = useMemo(
    () => societyPositions(data, myPlayerId), [data, myPlayerId]);
  const rows = useMemo(
    () => standingRows(data, myPlayerId, { sort, query }),
    [data, myPlayerId, sort, query]);
  const rounds = useMemo(
    () => filteredRounds(data, betFilter), [data, betFilter]);

  const maxAbs = useMemo(
    () => rows.reduce((m, r) => Math.max(m, Math.abs(r.net)), 0), [rows]);

  if (loading) {
    return (
      <div className="pr-steps">
        <div className="pr-skeleton" style={{ height: 140 }} />
        <div className="pr-skeleton" style={{ height: 180 }} />
      </div>
    );
  }

  if (loadError) {
    return <div className="pr-steps"><div className="pr-error">{loadError}</div></div>;
  }

  const searching = query.trim().length > 0;
  const visibleRows = searching || showAll ? rows : rows.slice(0, PLAYERS_N);
  const visibleMine = showAllMine ? mine : mine.slice(0, TOP_N);
  const visibleSociety = showAllSociety ? society : society.slice(0, TOP_N);

  return (
    <div className="pr-steps">
      {/* Which question this half is answering. */}
      <div className="pr-viewswitch">
        {[['players', 'By player'], ['rounds', 'By round']].map(([v, label]) => (
          <button key={v} type="button"
                  className={`pr-viewswitch-btn${
                    view === v ? ' is-selected' : ''}`}
                  onClick={() => setView(v)}>
            {label}
          </button>
        ))}
      </div>

      {view === 'players' ? (
        <>
          <Card>
            <div className="pr-standing-top">
              <div>
                <div className="pr-eyebrow">Season standing</div>
                <div className={`pr-hero${
                  season.net > 0 ? ' is-up' : season.net < 0 ? ' is-down' : ''}`}>
                  {season.net > 0 ? '+' : season.net < 0 ? '−' : ''}
                  {money(Math.abs(season.net))}
                </div>
              </div>
              <div className="pr-standing-tiles">
                <div>
                  <div className="pr-tile-label">Up on</div>
                  <div className="pr-tile-value is-up">{money(season.upOn)}</div>
                </div>
                <div>
                  <div className="pr-tile-label">Down to</div>
                  <div className="pr-tile-value is-down">
                    {money(season.downTo)}
                  </div>
                </div>
              </div>
            </div>

            <div className="pr-trend">
              <span className="pr-trend-caption">
                {trendCaption(points, money)}
              </span>
              <span className="pr-trend-bars">
                {points.map((p) => (
                  <span
                    key={p.key}
                    className={`pr-trend-bar${p.up ? ' is-up' : ' is-down'}`}
                    style={{ height: p.height }}
                    title={`${p.name}: ${p.cumulative >= 0 ? 'up' : 'down'} ${
                      money(Math.abs(p.cumulative))}`}
                  />
                ))}
              </span>
            </div>
          </Card>

          <div>
            <Eyebrow label="You v the society"
                     meta={`${mine.length} open ${
                       mine.length === 1 ? 'position' : 'positions'}`} />
            <Card list>
              {mine.length === 0 ? (
                <div className="pr-empty">
                  You are level with everyone.
                </div>
              ) : visibleMine.map((p) => (
                <div key={p.playerId} className="pr-row">
                  <Avatar name={nameOf(p.playerId)} size={34} />
                  <span className="pr-row-main">
                    <span className="pr-row-title">{nameOf(p.playerId)}</span>
                    <span className="pr-row-tags">
                      <span className={`pr-tag${
                        p.amount > 0 ? ' is-up' : ' is-down'}`}>
                        {p.amount > 0 ? 'UP' : 'DOWN'}
                      </span>
                      <span className="pr-row-sub">
                        {roundsLabel(data?.rounds_with?.[p.playerId] || 0)}
                      </span>
                    </span>
                  </span>
                  <span className={`pr-amount-figure${
                    p.amount > 0 ? ' is-up' : ' is-down'}`}>
                    {money(Math.abs(p.amount))}
                  </span>
                </div>
              ))}
            </Card>
            <MoreToggle expanded={showAllMine}
                        hidden={mine.length - TOP_N}
                        showLabel="Show top 10"
                        onToggle={() => setShowAllMine((v) => !v)} />
          </div>

          <div>
            <Eyebrow label="Across the society"
                     meta={`${society.length} ${
                       society.length === 1 ? 'position' : 'positions'}`} />
            <Card list>
              {society.length === 0 ? (
                <div className="pr-empty">
                  Nothing standing between anybody else.
                </div>
              ) : (
                <>
                  <div className="pr-society-head">
                    <span className="pr-col is-down">Down</span>
                    <span className="pr-col is-up">Up</span>
                    <span className="pr-col is-amount">Amount</span>
                  </div>
                  {visibleSociety.map((p) => (
                    <div key={`${p.down}:${p.up}`} className="pr-society-row">
                      <span className="pr-society-side">
                        <Avatar name={nameOf(p.down)} size={28} />
                        <span className="pr-society-name">
                          {shortName(nameOf(p.down))}
                        </span>
                      </span>
                      <span className="pr-society-arrow" aria-hidden="true">
                        →
                      </span>
                      <span className="pr-society-side">
                        <Avatar name={nameOf(p.up)} size={28} />
                        <span className="pr-society-name">
                          {shortName(nameOf(p.up))}
                        </span>
                      </span>
                      <span className="pr-society-amount">
                        {money(p.amount)}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </Card>
            <MoreToggle expanded={showAllSociety}
                        hidden={society.length - TOP_N}
                        showLabel="Show top 10"
                        onToggle={() => setShowAllSociety((v) => !v)} />
          </div>

          <div>
            <Eyebrow label="Standing by player"
                     meta={`${rows.length} ${
                       rows.length === 1 ? 'player' : 'players'}`} />
            <div className="pr-controls">
              <label className="pr-search">
                <span aria-hidden="true">⌕</span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search players"
                  aria-label="Search players"
                />
              </label>
              <button type="button" className="pr-sort"
                      onClick={() => setSort((current) => {
                        const i = SORTS.findIndex((s) => s.value === current);
                        return SORTS[(i + 1) % SORTS.length].value;
                      })}>
                ⇅ {SORTS.find((s) => s.value === sort).label}
              </button>
            </div>
            <Card list>
              <div className="pr-standing-head">
                <span className="pr-col">Player</span>
                <span className="pr-col is-centre">Down / Up</span>
                <span className="pr-col is-amount">Net</span>
              </div>
              {visibleRows.length === 0 ? (
                <div className="pr-empty">No players match that.</div>
              ) : visibleRows.map((r) => (
                <button key={r.playerId} type="button" className="pr-row"
                        onClick={() => setDetailId(r.playerId)}>
                  <Avatar name={r.name} size={30} />
                  <span className="pr-row-main">
                    <span className="pr-row-title">{r.name}</span>
                    <span className="pr-row-sub">
                      {playerSubline(data, myPlayerId, r.playerId, money)}
                    </span>
                  </span>
                  <span className="pr-bar" aria-hidden="true">
                    <span className="pr-bar-track" />
                    <span className="pr-bar-tick" />
                    <span className={`pr-bar-fill${
                      r.net >= 0 ? ' is-up' : ' is-down'}`}
                          style={{ width: `${barWidth(r.net, maxAbs)}%` }} />
                  </span>
                  <span className={`pr-amount-figure${
                    r.net > 0 ? ' is-up' : r.net < 0 ? ' is-down' : ''}`}>
                    {r.net > 0 ? '+' : r.net < 0 ? '−' : ''}
                    {money(Math.abs(r.net))}
                  </span>
                </button>
              ))}
            </Card>
            {!searching ? (
              <MoreToggle expanded={showAll}
                          hidden={rows.length - PLAYERS_N}
                          showLabel="Show fewer"
                          onToggle={() => setShowAll((v) => !v)} />
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="pr-rounds-head">
            <span className="pr-rounds-summary">{roundsSummary(data)}</span>
            <span className="pr-filter">
              {[['all', 'All bets'], ['mine', 'Just yours']].map(([v, l]) => (
                <button key={v} type="button"
                        className={`pr-filter-pill${
                          betFilter === v ? ' is-selected' : ''}`}
                        onClick={() => setBetFilter(v)}>
                  {l}
                </button>
              ))}
            </span>
          </div>

          {rounds.length === 0 ? (
            <Card><div className="pr-empty">Nothing played for yet.</div></Card>
          ) : rounds.map((round) => {
            const open = openRound === round.key;
            const myBets = round.bets.filter((b) => b.mine).length;
            return (
              <div key={round.key} className="pr-round-block">
                <button type="button" className="pr-round-header"
                        onClick={() => setOpenRound(open ? null : round.key)}>
                  <span className="pr-round-header-main">
                    <span className="pr-round-header-title">
                      {round.course_name || round.name}
                    </span>
                    <span className="pr-round-header-sub">
                      {[roundScope(round), formatRoundDate(round.date),
                        round.format].filter(Boolean).join(' · ')}
                    </span>
                    <span className="pr-round-header-meta">
                      {round.bets.length} {
                        round.bets.length === 1 ? 'bet' : 'bets'} · {myBets} yours
                    </span>
                  </span>
                  {(() => {
                    // The pill is YOUR position on this round, never the
                    // round's result — several bets on one round can finish
                    // differently, so there is no single result to give.
                    const pill = roundPill(round, myBets, money);
                    return (
                      <span className={`pr-round-pill${pill.tone}`}
                            title={pill.title}>
                        {pill.text}
                      </span>
                    );
                  })()}
                  <span className="pr-round-caret" aria-hidden="true">
                    {open ? '▾' : '▸'}
                  </span>
                </button>

                {open ? (
                  <div className="pr-round-body">
                    {round.bets.map((bet) => (
                      <div key={bet.id} className="pr-bet-row">
                        <span className="pr-row-main">
                          <span className="pr-bet-title">
                            {betTitle(bet, shortName)}
                            {bet.outcome === 'won' || bet.outcome === 'lost' ? (
                              <span className={`pr-tag${
                                bet.outcome === 'won' ? ' is-up' : ' is-down'}`}>
                                {bet.outcome.toUpperCase()}
                              </span>
                            ) : null}
                          </span>
                          <span className="pr-row-sub">
                            {betSubline(bet, money, shortName)}
                          </span>
                        </span>
                        <span className={`pr-bet-amount${
                          !bet.mine ? ' is-other'
                            : bet.amount > 0 ? ' is-up'
                              : bet.amount < 0 ? ' is-down' : ''}`}>
                          {!bet.mine || bet.amount === 0 ? '—'
                            : `${bet.amount > 0 ? '+' : '−'}${
                                money(Math.abs(bet.amount))}`}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </>
      )}

      {detailId ? (
        <PlayerSheet
          playerId={detailId}
          name={nameOf(detailId)}
          rounds={data?.rounds_with?.[detailId] || 0}
          vs={vsOf(data, myPlayerId, detailId)}
          net={netOf(data, detailId)}
          nameOf={nameOf}
          onClose={() => setDetailId(null)}
        />
      ) : null}
    </div>
  );
};

export default HistoryView;
